import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge, Card, cardClasses, Icon } from '@evogard/ui';
import {
  ZERO,
  careerRole,
  countsActivations,
  format,
  fromColumn,
  sum,
} from '@evogard/domain';
import type { AwardCategory, AwardLine, AwardState, Period } from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { periodLabel, refusalLabel } from './common.js';

/*
 * Os blocos das premiações do Início do Comercial — o total do mês, o cartão de
 * cada lote e o cartão de cada premiação. Moravam no Painel de Gestão, que saiu
 * em 28/10/2026: o Início assumiu o papel dele.
 */

/**
 * O total da competência, em destaque.
 *
 * ## Ele SOME quando qualquer premiação recusa
 *
 * Este é o número que alguém confere contra o contracheque: somar as premiações
 * que apuraram e ignorar as que recusaram produz um total que parece o do mês e
 * não é. A soma é feita sobre os totais que o SERVIDOR já calculou por lote —
 * cada um deles já carrega essa regra —, então a tela nunca soma uma linha que
 * o servidor se recusou a somar.
 *
 * Somar `1x` com `2x` é discutível — são dois lotes com calendários próprios —,
 * e aqui se soma mesmo assim: a pergunta é "quanto está em jogo neste mês", e
 * não o dia em que o dinheiro cai. O total de cada lote está no cartão dele.
 */
export function AwardsTotal({
  state,
  categories,
  label,
}: {
  state: AwardState;
  categories: AwardCategory[];
  /** O rótulo do total — "Acumulado de outubro de 2026". */
  label: string;
}) {
  const lines = categories.flatMap((c) => c.lines);
  const refused = lines.filter((l) => l.estado === 'recusado');

  /*
   * `fromColumn` e nunca `Number(texto)`: o total vem como `numeric(12,2)` em
   * texto, e dinheiro nesta casa é centavo inteiro. Um `Number` funciona em
   * todo teste e perde o centavo na conciliação do mês.
   */
  const total = categories.every((c) => c.total !== null)
    ? sum(...categories.map((c) => fromColumn(c.total) ?? ZERO))
    : null;

  const tone = total === null ? 'alerta' : state === 'garantido' ? 'sucesso' : 'erro';

  return (
    <div
      className={cardClasses({
        tone,
        className: 'flex items-center justify-between gap-4',
      })}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="rotulo">{label}</span>
        {total === null ? (
          <span className="text-sm text-texto-suave">
            {refused.length === lines.length
              ? 'Nenhuma premiação pôde ser apurada — o motivo está em cada uma delas.'
              : `${refused.length} de ${lines.length} premiações não puderam ser apuradas, então o total não é mostrado.`}
          </span>
        ) : (
          <span className="text-xl font-extrabold tabular-nums text-texto">
            {format(total)}
          </span>
        )}
      </div>

      <span
        className={
          total === null
            ? 'flex size-10 shrink-0 items-center justify-center rounded-full bg-superficie text-alerta'
            : state === 'garantido'
              ? 'flex size-10 shrink-0 items-center justify-center rounded-full bg-superficie text-sucesso'
              : 'flex size-10 shrink-0 items-center justify-center rounded-full bg-superficie text-erro'
        }
      >
        <Icon nome={total === null ? 'atencao' : 'dinheiro'} size={20} />
      </span>
    </div>
  );
}

/**
 * Um lote de pagamento — `1x` ou `2x` — com as premiações dele.
 *
 * O lote é o agrupamento porque é o que fecha junto, com calendário e nota
 * fiscal próprios: quem lê não pergunta "quanto rendeu esta premiação", pergunta
 * "quanto fecha neste lote". O corte vem do servidor — deixar a tela agrupar
 * faria a regra do total (só existe quando todas apuraram) viver em dois
 * lugares, e o segundo é o que envelhece.
 *
 * As premiações ficam numa coluna: as do mesmo lote se SOMAM, e duas colunas
 * fariam o olho ler em zigue-zague uma lista que só tem uma ordem.
 */
export function CategoryBlock({
  category,
  month,
  state,
  consultant,
  actions,
  footer,
}: {
  category: AwardCategory;
  month: string;
  state: AwardState;
  /** De quem é a carteira; ausente é a própria. */
  consultant: string | undefined;
  /** O que anda ao lado do total do lote — a nota fiscal do lote (28/10/2026). */
  actions?: ReactNode;
  /** O pé do cartão, centralizado — o botão do recibo do lote (28/10/2026). */
  footer?: ReactNode;
}) {
  return (
    <Card
      title={`Recorrência ${category.category}`}
      descricao={`Competência de ${periodLabel(month as Period)}.`}
      /*
       * O total do lote em DESTAQUE, no tamanho do total do alto (20/10/2026,
       * pedido do dono): num `Badge` ele sumia ao lado do título, e é o número
       * que fecha junto — o que se confere contra a nota de cada lote.
       */
      actions={
        <span className="flex items-start gap-3">
          {actions}
          {category.total === null ? (
            <Badge tone="alerta">sem total</Badge>
          ) : (
            <span className="flex flex-col items-end gap-0.5">
              <span className="rotulo text-texto-suave">Total do lote</span>
              <span
                className={
                  state === 'garantido'
                    ? 'text-xl font-extrabold tabular-nums text-sucesso'
                    : 'text-xl font-extrabold tabular-nums text-erro'
                }
              >
                <MoneyValue value={category.total} />
              </span>
            </span>
          )}
        </span>
      }
    >
      <div className="flex flex-col gap-3">
        {category.lines.map((line) => (
          <AwardCard
            key={line.id}
            line={line}
            month={month}
            state={state}
            consultant={consultant}
          />
        ))}
        {footer}
      </div>
    </Card>
  );
}

/**
 * Uma premiação — o cartão que abre o relatório.
 *
 * ## O clique só existe onde há o que conferir
 *
 * Premiação recusada não leva a lugar nenhum: a lista estaria vazia, e o que
 * falta já está escrito no cartão. Premiação com R$ 0,00 também não: sem valor
 * não há o que conferir, e o clique abriria uma tabela vazia. Um cartão que
 * reage ao ponteiro e não navega é a tela prometendo o que não cumpre, então o
 * realce entra junto com o link, nunca sozinho.
 *
 * ## O `state` viaja no link, e sem ele o relatório mentiria
 *
 * O endereço do relatório aceita os dois lados, e o padrão dele é `garantido`.
 * Um link sem o campo abriria a lista de boletos PAGOS sob o total de boletos
 * em ABERTO que o cartão mostra — 200, sem erro, com os números discordando.
 */
function AwardCard({
  line,
  month,
  state,
  consultant,
}: {
  line: AwardLine;
  month: string;
  state: AwardState;
  consultant: string | undefined;
}) {
  const refused = line.estado === 'recusado';
  const value = line.estado === 'apurado' ? fromColumn(line.amount) : null;
  const linked = value !== null && value !== 0;
  const uncounted = productsNotCounted(line);

  /*
   * `rounded-borda` e não `rounded-cartao`: isto é uma linha dentro de um
   * cartão, e cartão dentro de cartão está sempre errado — a padronização de
   * 17/09 fixou as duas formas.
   */
  const shell = refused
    ? 'flex flex-col gap-2 rounded-borda border border-alerta bg-alerta-suave p-4'
    : uncounted
      ? 'group relative flex flex-col gap-2 rounded-borda border border-alerta/30 bg-superficie-suave p-4'
      : 'flex flex-col gap-2 rounded-borda border border-borda bg-superficie-suave p-4';
  const classes = linked
    ? `${shell} transition-colors hover:border-borda-forte hover:bg-superficie`
    : shell;

  const content = (
    <>
      <div className="flex items-center gap-2.5">
        <span
          className={
            refused
              ? 'flex size-8 shrink-0 items-center justify-center rounded-full bg-superficie text-alerta'
              : state === 'garantido'
                ? 'flex size-8 shrink-0 items-center justify-center rounded-full bg-superficie text-sucesso'
                : 'flex size-8 shrink-0 items-center justify-center rounded-full bg-superficie text-erro'
          }
        >
          <Icon nome={refused ? 'atencao' : 'documento'} size={20} />
        </span>
        <span className="flex min-w-0 flex-col">
          {/*
           * Na Antecipada Plano de Carreira o título leva o cargo que a contagem
           * alcançou — "Antecipada Plano de Carreira Líder" —, como o legado
           * mostra. Abaixo de 30 placas não há cargo, e o título fica sozinho.
           */}
          <span className="text-sm font-semibold text-texto">
            {line.family === 'antecipada' &&
            line.plates !== null &&
            careerRole(line.plates)
              ? `${line.title} ${careerRole(line.plates)}`
              : line.title}
          </span>
        </span>
      </div>

      {line.estado === 'apurado' ? (
        <span className="text-lg font-extrabold text-texto">
          <MoneyValue value={line.amount} />
        </span>
      ) : (
        <span className="text-sm font-medium text-alerta">
          {refusalLabel(line.reason)}
        </span>
      )}

      {/*
       * A origem do dinheiro, e não uma previsão de recebimento: não há ciclo
       * com data nesta casa, e escrever uma seria a tela afirmando o que o
       * código não cumpre.
       */}
      <span className="text-xs text-texto-suave">{lineOrigin(line, month)}</span>
    </>
  );

  /*
   * O zero que é "não sei" ganha a dica no ponteiro e no foco — por isso o
   * `tabIndex`: sem ele, quem navega pelo teclado nunca leria o aviso.
   */
  if (uncounted) {
    const tipId = `award-${line.id}-uncounted`;
    return (
      <div className={classes} tabIndex={0} aria-describedby={tipId}>
        {content}
        <span
          id={tipId}
          role="tooltip"
          data-chrome
          className="pointer-events-none invisible absolute top-full left-1/2 z-50 mt-2 w-max max-w-72 -translate-x-1/2 rounded-borda bg-chrome px-3 py-2 text-xs text-chrome-texto opacity-0 shadow-flutuante transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
        >
          Não há produtos contados nesta premiação:{' '}
          {line.uncheckedVehicles === 1
            ? '1 veículo ainda não teve os produtos consultados no SGA.'
            : `${line.uncheckedVehicles} veículos ainda não tiveram os produtos consultados no SGA.`}
        </span>
      </div>
    );
  }

  if (!linked) return <div className={classes}>{content}</div>;

  return (
    <Link
      to="/sales/management/portfolio"
      /*
       * `consultant` viaja junto: sem ele, a lista de boletos da carteira de
       * outra pessoa abriria a de quem está olhando, sob o título dela.
       */
      search={{ award: line.id, month, state, consultant, origin: 'inicio' }}
      /* o nome da tela de onde se veio, para o voltar do relatório */
      state={{ backLabel: 'Início' }}
      className={classes}
    >
      {content}
    </Link>
  );
}

/**
 * A premiação POR PRODUTO em R$ 0,00 porque nenhum veículo teve os produtos
 * consultados (03/11/2026, pedido do dono). O zero dela é "não sei", e não
 * "não alcançou": o veículo nunca consultado fica fora do valor e só entra em
 * `uncheckedVehicles` — ver `productLine` no serviço do painel. O Início a
 * mostra com a borda amarela fraca, e não a esconde com as outras de zero.
 */
export function productsNotCounted(line: AwardLine): boolean {
  return (
    line.family === 'produto' &&
    line.estado === 'apurado' &&
    (fromColumn(line.amount) ?? 0) === 0 &&
    (line.uncheckedVehicles ?? 0) > 0
  );
}

/**
 * A origem do dinheiro de uma premiação.
 *
 * O substantivo muda com a família, e não é enfeite: a recorrência conta BOLETO
 * pago, a direta conta CONTRATO novo. "Boletos de setembro" numa premiação
 * direta é a tela afirmando o que o cálculo não fez. A direta conta os
 * contratos da COMPETÊNCIA dela, que com 45 dias é o mês anterior ao da tela
 * (`directCompetence`), e diz qual — senão a soma pareceria do mês aberto.
 */
function lineOrigin(line: AwardLine, month: string): string {
  const direct = countsActivations(line.family);
  const noun = direct
    ? 'Contratos'
    : line.family === 'produto'
      ? 'Veículos com boleto pago'
      : 'Boletos';
  const of = periodLabel((direct ? (line.competence ?? month) : month) as Period);
  return `${noun} de ${of}`;
}
